import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';

interface OnboardingGateValue {
  /**
   * True while the first-login wizard owns the screen (or is still deciding
   * whether it will). The tour provider reads this to hold off, the same way
   * it yields to the patch-log modal.
   */
  isBlocking: boolean;
  setBlocking: (blocking: boolean) => void;
  /** Bumped by "Pengenalan awal" in the help menu: open the wizard regardless of status. */
  openRequest: number;
  openOnboarding: () => void;
  /**
   * Set once the wizard has closed in this mount, deferred or finished. A page
   * tour auto-starting 450 ms after "Lanjutkan nanti" would be one overlay
   * too many; the tours come back on the next login.
   */
  suppressPageTours: boolean;
  setSuppressPageTours: (suppress: boolean) => void;
}

const OnboardingGateContext = createContext<OnboardingGateValue | null>(null);

/**
 * Sits ABOVE TourProvider (like PatchLogGateProvider) so the provider can read
 * the flag without depending on the gate component.
 */
export const OnboardingGateProvider = ({ children }: { children: ReactNode }) => {
  const [isBlocking, setBlocking] = useState(false);
  const [openRequest, setOpenRequest] = useState(0);
  const [suppressPageTours, setSuppressPageTours] = useState(false);
  const openOnboarding = useCallback(() => setOpenRequest((previous) => previous + 1), []);
  const value = useMemo<OnboardingGateValue>(
    () => ({ isBlocking, setBlocking, openRequest, openOnboarding, suppressPageTours, setSuppressPageTours }),
    [isBlocking, openRequest, openOnboarding, suppressPageTours]
  );
  return <OnboardingGateContext.Provider value={value}>{children}</OnboardingGateContext.Provider>;
};

/** Null outside the provider — isolated surfaces and tests tolerate that. */
export const useOptionalOnboardingGate = () => useContext(OnboardingGateContext);
